'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { documentSlug } from '@/lib/utils/documentMetadata';
import legislationServices from '@/services/legislation-services';
import type { LegislationHistoryInterface } from '@/types/api-types';
import { History, Loader2 } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useState } from 'react';

interface LegislationHistoryProps {
  iri: string;
  className?: string;
  containerClassName?: string;
}

export function LegislationHistory({
  iri,
  className,
  containerClassName,
}: LegislationHistoryProps) {
  const [history, setHistory] = useState<LegislationHistoryInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const locale = useLocale();
  const t = useTranslations('LegislationDetails.legislationHistory');
  const tCommon = useTranslations('Common');

  const fetchHistory = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const segments = iri.split('/');
      if (segments.length === 5) {
        const cleanIri = `/${segments.slice(0, 4).join('/')}`;
        const response =
          await legislationServices.getLegislationHistory(cleanIri);

        if (Array.isArray(response.data)) {
          setHistory(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching legislation history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isCurrentLegislation = (legislationIri: string) => {
    return legislationIri === `/${iri}`;
  };

  function buildInstrumentUrl(locale: string, text: string) {
    const match = text.match(/^\s*(\d{1,4})\/(\d{1,4})/);
    if (!match) {
      throw new Error(`Cannot parse novela link from "${text}"`);
    }
    const [, novelaLinkNumber, novelaLinkYear] = match;
    return `/${locale}/${documentSlug(locale)}/SK/ZZ/${novelaLinkYear}/${novelaLinkNumber}`;
  }

  return (
    <div className={cn('flex justify-end', containerClassName)}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger
          asChild
          onClick={() => {
            fetchHistory();
            setIsOpen(true);
          }}
        >
          <button
            id='legislation-history-trigger'
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2.5 bg-surface-card border-2 border-brand-primary-color rounded-lg hover:bg-brand-primary-color/5 transition-all duration-200 font-medium text-brand-primary-color',
              className
            )}
            title={tCommon('viewLegislationHistory')}
          >
            <History className='h-4 w-4' />
            <span className='text-sm'>{t('title')}</span>
          </button>
        </DialogTrigger>
        <DialogContent className='sm:max-w-lg max-h-[80vh] p-0 gap-0 overflow-hidden'>
          <DialogHeader className='px-4 py-3 border-b bg-surface-subtle/80'>
            <div className='flex items-center gap-2'>
              <History className='h-4 w-4 text-brand-primary-color' />
              <DialogTitle className='text-base font-semibold text-content-primary'>
                {t('title')}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div
            className='overflow-y-auto'
            style={{ maxHeight: 'calc(80vh - 52px)' }}
          >
            {isLoading ? (
              <div className='flex flex-col justify-center items-center py-16 gap-3'>
                <Loader2 className='h-6 w-6 animate-spin text-brand-primary-color' />
                <p className='text-sm text-content-muted'>{t('loading')}</p>
              </div>
            ) : history.length === 0 ? (
              <div className='text-center py-16 px-4'>
                <History className='h-10 w-10 text-content-disabled mx-auto mb-3' />
                <p className='text-sm text-content-secondary font-medium'>
                  {t('noHistory')}
                </p>
              </div>
            ) : (
              <div className='px-4 py-3'>
                {/* Table Header */}
                <div className='grid grid-cols-[auto_1fr_1fr] gap-x-4 pb-2 border-b border-line-default items-center'>
                  <div className='w-6' />
                  <div className='text-sm font-medium text-content-secondary'>
                    {t('dateOfEffectiveness')}
                  </div>
                  <div className='text-sm font-medium text-content-secondary text-center'>
                    {t('amendment')}
                  </div>
                </div>

                {/* Table Body */}
                <div className='divide-y divide-line-subtle'>
                  {history.map((legislation, idx) => {
                    const isCurrent = isCurrentLegislation(legislation.Iri);
                    const amendments = legislation.Novela.split(',')
                      .map((n) => n.trim())
                      .filter(Boolean);

                    // Use the pre-formatted "Dátum účinnosti" field from API
                    const dateDisplay = legislation['Dátum účinnosti'];

                    // Check if this is the announced version (vyhlasene_znenie)
                    const isAnnouncedVersion =
                      legislation.Iri.includes('vyhlasene_znenie');

                    return (
                      <div
                        key={idx}
                        className={cn(
                          'grid grid-cols-[auto_1fr_1fr] gap-x-4 py-2.5 items-center',
                          isCurrent &&
                            'bg-brand-primary-color/10 border border-brand-primary-color/30 rounded -mx-2 px-2'
                        )}
                      >
                        {/* Row Number */}
                        <div
                          className={cn(
                            'w-6 text-sm font-medium text-center',
                            isCurrent
                              ? 'text-brand-primary-color'
                              : 'text-content-muted'
                          )}
                        >
                          {idx + 1}.
                        </div>

                        {/* Date Range */}
                        <div>
                          {isCurrent ? (
                            <span className='text-sm font-medium text-brand-primary-color'>
                              {dateDisplay}
                            </span>
                          ) : (
                            <Link
                              href={`/${locale}/${documentSlug(locale)}/${legislation.Iri.startsWith('/') ? legislation.Iri.substring(1) : legislation.Iri}`}
                              onClick={(e) => {
                                if (
                                  !e.ctrlKey &&
                                  !e.metaKey &&
                                  e.button === 0
                                ) {
                                  setIsOpen(false);
                                }
                              }}
                              className='text-sm font-medium text-brand-primary-color hover:underline'
                              title={tCommon('loadThisLegislationVersion')}
                            >
                              {dateDisplay}
                            </Link>
                          )}
                        </div>

                        {/* Amendments */}
                        <div className='flex flex-wrap gap-1 justify-center'>
                          {amendments.length > 0 ? (
                            amendments.map((novela, i) => {
                              try {
                                const novelaLink = buildInstrumentUrl(
                                  locale,
                                  novela
                                );
                                return (
                                  <Link
                                    key={i}
                                    href={novelaLink}
                                    target='_blank'
                                    className='text-xs text-brand-primary-color hover:underline'
                                  >
                                    {novela}
                                    {i < amendments.length - 1 && ','}
                                  </Link>
                                );
                              } catch {
                                return (
                                  <span
                                    key={i}
                                    className='text-xs text-content-muted'
                                  >
                                    {novela}
                                    {i < amendments.length - 1 && ','}
                                  </span>
                                );
                              }
                            })
                          ) : isAnnouncedVersion ? (
                            <span
                              className={cn(
                                'text-xs',
                                isCurrent
                                  ? 'text-brand-primary-color font-medium'
                                  : 'text-content-muted'
                              )}
                            >
                              -
                            </span>
                          ) : (
                            <span className='text-xs text-content-muted'>
                              -
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
